import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import type { Ticket, ApiResponse } from '../types';
import { Clock, CheckCircle, AlertCircle, Plus, X, Trash2, Pencil, Download, ShieldCheck } from 'lucide-react';
import ConfirmModal from '../components/ConfirmModal';
import VoiceAiBadge from '../components/VoiceAiBadge';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';

export default function WorkLogsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');
  const [pendingReviewFilter, setPendingReviewFilter] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['work-logs', startDateFilter, endDateFilter, pendingReviewFilter],
    queryFn: () => api.getWorkLogs({
      start_date: startDateFilter || undefined,
      end_date: endDateFilter || undefined,
      requires_review: pendingReviewFilter ? true : undefined,
      limit: 100,
    }),
  });

  const { data: ticketsData } = useQuery({
    queryKey: ['tickets-for-select'],
    queryFn: () => api.getTickets({ limit: 100 }),
  });

  const tickets = (ticketsData as ApiResponse<Ticket[]>)?.data ?? [];

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteWorkLog(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['work-logs'] }),
  });

  const reviewMutation = useMutation({
    mutationFn: (id: string) => api.reviewWorkLog(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['work-logs'] }),
  });

  const batchReviewMutation = useMutation({
    mutationFn: (ids: string[]) => api.batchReviewWorkLogs(ids),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['work-logs'] }),
  });

  const isAdmin = user?.role === 'admin';
  const isInternal = user && ['admin', 'agent'].includes(user.role);
  const canCreate = isInternal;
  const canEditDelete = isInternal;
  const canExport = user && ['admin', 'customer_manager'].includes(user.role);
  const showTimeColumns = isInternal;

  const workLogs = data?.data ?? [];
  const totalMinutes = workLogs.reduce((s, wl) => s + (wl.duration_minutes || 0), 0);
  const billableMinutes = workLogs.filter(wl => wl.billable).reduce((s, wl) => s + (wl.duration_minutes || 0), 0);

  const pendingReviewLogs = workLogs.filter(wl => wl.requires_review && !wl.reviewed);

  const handleExport = async () => {
    const token = localStorage.getItem('auth_token');
    const url = api.getExportWorkLogsUrl({
      start_date: startDateFilter || undefined,
      end_date: endDateFilter || undefined,
    });
    try {
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `work-logs-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Export failed', err);
    }
  };

  const handleBatchReview = () => {
    const ids = pendingReviewLogs.map(wl => wl.id);
    if (ids.length > 0) {
      batchReviewMutation.mutate(ids);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="page-title">{t('workLogs.title')}</h1>
        <div className="flex items-center gap-3">
          {canExport && (
            <button onClick={handleExport} className="btn bg-success-600 text-white hover:bg-success-700 flex items-center gap-2">
              <Download size={18} /> {t('common.exportCsv')}
            </button>
          )}
          {canCreate && (
            <button className="btn btn-primary flex items-center gap-2" onClick={() => setShowCreate(true)}>
              <Plus size={18} /> <span>{t('workLogs.newWorkLog')}</span>
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <label className="text-sm text-surface-500">{t('workLogs.filterFromDate')}</label>
          <input type="date" className="input py-2 text-sm" value={startDateFilter} onChange={(e) => setStartDateFilter(e.target.value)} />
        </div>
        <div>
          <label className="text-sm text-surface-500">{t('workLogs.filterToDate')}</label>
          <input type="date" className="input py-2 text-sm" value={endDateFilter} onChange={(e) => setEndDateFilter(e.target.value)} />
        </div>
        {isAdmin && (
          <div className="tabs-pill mt-5 self-end">
            <button
              type="button"
              onClick={() => setPendingReviewFilter(false)}
              className={!pendingReviewFilter ? 'tab-pill-active' : 'tab-pill-inactive'}
            >
              {t('tickets.all')}
            </button>
            <button
              type="button"
              onClick={() => setPendingReviewFilter(true)}
              className={pendingReviewFilter ? 'tab-pill-active' : 'tab-pill-inactive'}
            >
              {t('workLogs.filterPendingReview')}
            </button>
          </div>
        )}
        {(startDateFilter || endDateFilter || pendingReviewFilter) && (
          <button onClick={() => { setStartDateFilter(''); setEndDateFilter(''); setPendingReviewFilter(false); }}
            className="btn btn-secondary py-2 text-sm mt-5">
            {t('common.clearFilter')}
          </button>
        )}
      </div>

      {/* Batch review button */}
      {isAdmin && pendingReviewFilter && pendingReviewLogs.length > 0 && (
        <div className="flex items-center gap-3">
          <button
            onClick={handleBatchReview}
            disabled={batchReviewMutation.isPending}
            className="btn bg-success-600 text-white hover:bg-success-700 flex items-center gap-2"
          >
            <ShieldCheck size={18} />
            {batchReviewMutation.isPending ? t('common.updating') : `${t('workLogs.reviewAll')} (${pendingReviewLogs.length})`}
          </button>
          {batchReviewMutation.isError && (
            <span className="text-danger-600 text-sm">{t('workLogs.batchReviewError')}</span>
          )}
        </div>
      )}

      {/* Summary — always visible, even when no logs exist */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card bg-info-50 border border-info-100">
          <p className="text-sm text-info-600">{t('workLogs.totalHours')}</p>
          <p className="text-xl font-bold text-blue-900 tabular-nums font-display">{(totalMinutes / 60).toFixed(1)}</p>
        </div>
        <div className="card bg-success-50 border border-success-100">
          <p className="text-sm text-success-600">{t('workLogs.billableHours')}</p>
          <p className="text-xl font-bold text-green-900 tabular-nums font-display">{(billableMinutes / 60).toFixed(1)}</p>
        </div>
        <div className="card bg-purple-50 border border-purple-100">
          <p className="text-sm text-purple-600">{t('common.records')}</p>
          <p className="text-xl font-bold text-purple-900 tabular-nums font-display">{workLogs.length}</p>
        </div>
      </div>

      {showCreate && (
        <WorkLogFormModal tickets={tickets} onClose={() => setShowCreate(false)}
          onSuccess={() => { queryClient.invalidateQueries({ queryKey: ['work-logs'] }); setShowCreate(false); }} />
      )}

      {editingId && (
        <EditWorkLogModal workLogId={editingId} onClose={() => setEditingId(null)}
          onSuccess={() => { queryClient.invalidateQueries({ queryKey: ['work-logs'] }); setEditingId(null); }} />
      )}

      {isLoading && (
        <div className="table-container">
          <div className="divide-y divide-surface-400/10">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="skeleton-table-row">
                <div className="skeleton-avatar" />
                <div className="skeleton-cell" />
                <div className="skeleton-cell max-w-[100px] flex-none" />
              </div>
            ))}
          </div>
        </div>
      )}
      {error && <div className="card bg-danger-50 border border-danger-100"><p className="text-danger-700">{t('workLogs.loadError')}</p></div>}

      {!isLoading && data?.success && (
        <div className="table-container">
          <table className="table-modern">
            <thead>
              <tr>
                <th className="text-start">{t('workLogs.ticket')}</th>
                <th className="text-start">{t('tickets.agent')}</th>
                <th className="text-start">{t('ticketDetail.summary')}</th>
                <th className="text-start">{t('workLogs.duration')}</th>
                <th className="text-start">{t('workLogs.review')}</th>
                {showTimeColumns && (
                  <>
                    <th className="text-start">{t('ticketDetail.startTime')}</th>
                    <th className="text-start">{t('ticketDetail.endTime')}</th>
                  </>
                )}
                <th className="text-start">{t('contacts.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {workLogs.length > 0 ? workLogs.map((log) => (
                <tr key={log.id}>
                  <td>
                    <div className="flex items-start gap-2">
                      <div className={`p-2 rounded-lg flex-shrink-0 ${log.source === 'voice_ai' ? 'bg-purple-100' : log.source === 'timer' ? 'bg-success-100' : 'bg-info-100'}`}>
                        <Clock className={log.source === 'voice_ai' ? 'text-purple-600' : log.source === 'timer' ? 'text-success-600' : 'text-info-600'} size={20} />
                      </div>
                      <span className="font-semibold text-surface-900">{log.ticket_title || t('workLogs.noTitle')}</span>
                    </div>
                  </td>
                  <td className="text-surface-600">{log.agent_name}</td>
                  <td className="text-surface-700 max-w-xs">
                    <p className="line-clamp-2">{log.summary}</p>
                  </td>
                  <td className="tabular-nums font-display text-primary-700 font-medium whitespace-nowrap">
                    <span>{log.duration_minutes} {t('common.minutes')}</span>
                    {log.source === 'voice_ai' && (
                      <div className="mt-1"><VoiceAiBadge confidence={log.ai_confidence} /></div>
                    )}
                  </td>
                  <td>
                    <div className="flex flex-col gap-2 items-start">
                      {log.requires_review && !log.reviewed && (
                        <span className="flex items-center gap-1 px-3 py-1 bg-warning-100 text-yellow-800 rounded-full text-xs">
                          <AlertCircle size={14} /> {t('workLogs.pendingReview')}
                        </span>
                      )}
                      {log.reviewed && (
                        <span className="flex items-center gap-1 px-3 py-1 bg-success-100 text-green-800 rounded-full text-xs">
                          <CheckCircle size={14} /> {t('workLogs.reviewed')}
                        </span>
                      )}
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${log.billable ? 'bg-info-100 text-blue-800' : 'bg-surface-100 text-surface-800'}`}>
                        {log.billable ? t('common.billable') : t('common.nonBillable')}
                      </span>
                      {isAdmin && log.requires_review && !log.reviewed && (
                        <button
                          type="button"
                          onClick={() => reviewMutation.mutate(log.id)}
                          disabled={reviewMutation.isPending}
                          className="btn py-1 px-2 bg-success-50 text-success-700 hover:bg-success-100 border border-success-200 text-xs flex items-center gap-1"
                          aria-label={t('workLogs.markReviewed')}
                        >
                          <ShieldCheck size={14} /> {t('workLogs.markReviewed')}
                        </button>
                      )}
                    </div>
                  </td>
                  {showTimeColumns && (
                    <>
                      <td className="text-surface-600 text-xs whitespace-nowrap">
                        {log.start_time ? format(new Date(log.start_time), 'dd/MM/yyyy HH:mm', { locale: he }) : '—'}
                      </td>
                      <td className="text-surface-600 text-xs whitespace-nowrap">
                        {log.end_time ? format(new Date(log.end_time), 'dd/MM/yyyy HH:mm', { locale: he }) : '—'}
                      </td>
                    </>
                  )}
                  <td>
                    <div className="flex items-center gap-1">
                      {canEditDelete && (
                        <>
                          <button type="button" onClick={() => setEditingId(log.id)} className="p-1 text-surface-400 hover:text-info-600 transition-colors" aria-label={t('workLogs.editTitle')}>
                            <Pencil size={16} />
                          </button>
                          <button type="button" onClick={() => setConfirmDeleteId(log.id)}
                            className="p-1 text-surface-400 hover:text-danger-600 transition-colors" aria-label={t('workLogs.deleteTitle')}>
                            <Trash2 size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={showTimeColumns ? 8 : 6}>
                    <div className="empty-state py-12">
                      <div className="empty-state-icon">
                        <Clock className="w-full h-full" />
                      </div>
                      <p className="empty-state-title">{t('workLogs.noWorkLogs')}</p>
                      <p className="empty-state-text">{t('workLogs.title')}</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {deleteMutation.isError && (
        <div className="bg-danger-50 border border-danger-100 text-danger-700 px-4 py-3 rounded-lg text-sm">{t('workLogs.deleteError')}</div>
      )}

      {reviewMutation.isError && (
        <div className="bg-danger-50 border border-danger-100 text-danger-700 px-4 py-3 rounded-lg text-sm">{t('workLogs.reviewError')}</div>
      )}

      {confirmDeleteId && (
        <ConfirmModal
          title={t('workLogs.deleteTitle')}
          description={t('workLogs.deleteConfirm')}
          confirmLabel={t('common.delete')}
          onConfirm={() => { deleteMutation.mutate(confirmDeleteId); setConfirmDeleteId(null); }}
          onCancel={() => setConfirmDeleteId(null)}
          danger
        />
      )}
    </div>
  );
}

function WorkLogFormModal({ tickets, onClose, onSuccess }: {
  tickets: Ticket[]; onClose: () => void; onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [ticketId, setTicketId] = useState('');
  const [summary, setSummary] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [billable, setBillable] = useState(true);

  const mutation = useMutation({
    mutationFn: () => api.createWorkLog({
      ticket_id: ticketId, start_time: new Date(startTime).toISOString(),
      end_time: new Date(endTime).toISOString(), summary, billable,
    }),
    onSuccess,
  });

  return (
    <div className="modal-backdrop">
      <div className="modal-content max-w-lg w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-surface-900">{t('workLogs.newTitle')}</h2>
          <button onClick={onClose} className="text-surface-400 hover:text-surface-600"><X size={24} /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }} className="space-y-4">
          <div>
            <label className="label">{t('workLogs.ticket')}</label>
            <select className="input" value={ticketId} onChange={(e) => setTicketId(e.target.value)} required>
              <option value="">{t('workLogs.selectTicket')}</option>
              {tickets.map((ticket) => <option key={ticket.id} value={ticket.id}>{ticket.title}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">{t('ticketDetail.startTime')}</label><input type="datetime-local" className="input" value={startTime} onChange={(e) => setStartTime(e.target.value)} required /></div>
            <div><label className="label">{t('ticketDetail.endTime')}</label><input type="datetime-local" className="input" value={endTime} onChange={(e) => setEndTime(e.target.value)} required /></div>
          </div>
          <div><label className="label">{t('ticketDetail.summary')}</label><textarea className="input min-h-[100px]" value={summary} onChange={(e) => setSummary(e.target.value)} required minLength={5} rows={4} /></div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="billable" checked={billable} onChange={(e) => setBillable(e.target.checked)} className="w-4 h-4" />
            <label htmlFor="billable" className="text-sm text-surface-700">{t('common.billable')}</label>
          </div>
          {mutation.error && <div className="bg-danger-50 border border-danger-100 text-danger-700 px-4 py-3 rounded-lg text-sm">{(mutation.error as any)?.error?.message || t('workLogs.createError')}</div>}
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={mutation.isPending} className="btn btn-primary flex-1">{mutation.isPending ? t('common.creating') : t('ticketDetail.createWorkLog')}</button>
            <button type="button" onClick={onClose} className="btn btn-secondary flex-1">{t('common.cancel')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditWorkLogModal({ workLogId, onClose, onSuccess }: {
  workLogId: string; onClose: () => void; onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [summary, setSummary] = useState('');
  const [billable, setBillable] = useState(true);

  const { data } = useQuery({
    queryKey: ['work-log', workLogId],
    queryFn: () => api.getWorkLog(workLogId),
  });

  useEffect(() => {
    if (data?.success && data.data) {
      setSummary(data.data.summary);
      setBillable(data.data.billable);
    }
  }, [data?.data?.id]);

  const mutation = useMutation({
    mutationFn: () => api.updateWorkLog(workLogId, { summary, billable }),
    onSuccess,
  });

  return (
    <div className="modal-backdrop">
      <div className="modal-content max-w-lg w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-surface-900">{t('workLogs.editTitle')}</h2>
          <button onClick={onClose} className="text-surface-400 hover:text-surface-600"><X size={24} /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }} className="space-y-4">
          <div><label className="label">{t('ticketDetail.summary')}</label><textarea className="input min-h-[100px]" value={summary} onChange={(e) => setSummary(e.target.value)} required minLength={5} rows={4} /></div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="edit-billable" checked={billable} onChange={(e) => setBillable(e.target.checked)} className="w-4 h-4" />
            <label htmlFor="edit-billable" className="text-sm text-surface-700">{t('common.billable')}</label>
          </div>
          {mutation.error && <div className="bg-danger-50 border border-danger-100 text-danger-700 px-4 py-3 rounded-lg text-sm">{(mutation.error as any)?.error?.message || t('workLogs.updateError')}</div>}
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={mutation.isPending} className="btn btn-primary flex-1">{mutation.isPending ? t('common.updating') : t('common.update')}</button>
            <button type="button" onClick={onClose} className="btn btn-secondary flex-1">{t('common.cancel')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
